import React, { useCallback, useEffect, useState, lazy } from 'react';
import { X, ArrowUpRight, Loader2 } from 'lucide-react';
interface Project {
  id: number;
  title: string;
  category: string;
  year: string;
  image: string;
  detailImages?: string[];
}
interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ProjectModal({
  project,
  isOpen,
  onClose
}: ProjectModalProps) {
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const images = project?.detailImages && project.detailImages.length > 0 ? project.detailImages : project ? [project.image] : [];
  // Reset loading state when project changes or modal opens
  useEffect(() => {
    if (isOpen && project) {
      setLoadedCount(0);
      setTotalImages(images.length);
    }
  }, [isOpen, project?.id]);
  const allLoaded = totalImages > 0 && loadedCount >= totalImages;
  const handleImageLoad = useCallback(() => {
    setLoadedCount(prev => prev + 1);
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  if (!isOpen || !project) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center" data-id="element-106">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-sm transition-opacity duration-300" onClick={onClose} data-id="element-107" />

      {/* Close Button */}
      <button onClick={onClose} className="fixed top-6 right-6 z-[110] p-2 text-white hover:text-yellow-400 transition-colors bg-black/50 rounded-full" data-id="element-108">
        <X size={32} data-id="element-109" />
      </button>

      {/* Loading Overlay */}
      {!allLoaded && <div className="fixed inset-0 z-[108] flex flex-col items-center justify-center bg-black/80 pointer-events-none" data-id="element-110">
          <Loader2 className="w-12 h-12 text-yellow-400 animate-spin mb-4" data-id="element-111" />
          <p className="text-white font-bold uppercase tracking-wider text-sm" data-id="element-112">
            Loading {loadedCount}/{totalImages}
          </p>
        </div>}

      {/* Modal Content */}
      <div className="relative z-[105] w-full h-full overflow-y-auto pt-24 pb-12 px-4 md:px-6" data-id="element-113">
        <div className="max-w-4xl mx-auto bg-black border border-white/10 min-h-screen" data-id="element-114">
          {/* Header */}
          <div className="p-8 md:p-12 border-b border-white/10" data-id="element-115">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-116">
              <div data-id="element-117">
                <div className="flex items-center gap-4 mb-4" data-id="element-118">
                  <span className="bg-yellow-400 text-black text-xs font-bold px-3 py-1 uppercase tracking-wider" data-id="element-119">
                    {project.category}
                  </span>
                  <span className="text-gray-400 font-bold text-sm" data-id="element-120">
                    {project.year}
                  </span>
                </div>
                <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter" data-id="element-121">
                  {project.title}
                </h2>
              </div>
            </div>

            <p className="text-xl text-gray-400 leading-relaxed max-w-2xl" data-id="element-122">
              A comprehensive brand identity and digital experience designed to
              push boundaries. We focused on creating a visual language that
              speaks to the modern consumer while maintaining timeless appeal.
            </p>
          </div>

          {/* Vertical Images - No gaps, lazy loaded */}
          <div className="w-full flex flex-col" data-id="element-123">
            {images.map((img, index) => <img key={index} src={img} alt={`${project.title} - ${index + 1}`} loading="lazy" onLoad={handleImageLoad} className={`w-full h-auto block transition-opacity duration-500 ${allLoaded ? 'opacity-100' : 'opacity-0'}`} style={{
            display: 'block',
            margin: 0,
            padding: 0
          }} data-id="element-124" />)}
          </div>

          {/* Footer of Modal */}
          <div className="p-12 border-t border-white/10 text-center" data-id="element-125">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-126">
              Ready to start your project?
            </h3>
            <button onClick={onClose} className="inline-block bg-yellow-400 text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-colors" data-id="element-127">
              Close Project
            </button>
          </div>
        </div>
      </div>
    </div>;
}